import React, { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX, Music } from 'lucide-react';
import { Button } from './ui/button';

const BackgroundMusic = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const hasAttemptedAutoplay = useRef(false);

  useEffect(() => {
    // Tocar automaticamente ao carregar
    const attemptAutoplay = async () => {
      if (audioRef.current && !hasAttemptedAutoplay.current) {
        hasAttemptedAutoplay.current = true;
        audioRef.current.volume = 0.3; // Volume inicial 30%
        audioRef.current.muted = false;
        
        try {
          // Tentar com som
          await audioRef.current.play();
          setIsPlaying(true);
          console.log("Música iniciada automaticamente!");
        } catch (error) {
          console.log("Autoplay bloqueado pelo navegador - tentando com mute");
          // Se falhar, tentar com mute primeiro
          try {
            audioRef.current.muted = true;
            await audioRef.current.play();
            // Depois desmutar
            setTimeout(() => {
              if (audioRef.current) {
                audioRef.current.muted = false;
              }
            }, 100);
            setIsPlaying(true);
          } catch (error2) {
            console.log("Autoplay prevented - esperando interação do usuário");
            setIsPlaying(false);
          }
        }
      }
    };

    // Tentar tocar imediatamente
    setTimeout(attemptAutoplay, 100);
    
    // Adicionar listener para primeira interação do usuário (fallback)
    const enableAudioOnInteraction = async () => {
      if (audioRef.current && !isPlaying) {
        try {
          audioRef.current.muted = false;
          await audioRef.current.play();
          setIsPlaying(true);
          // Remover listeners após sucesso
          document.removeEventListener('click', enableAudioOnInteraction);
          document.removeEventListener('touchstart', enableAudioOnInteraction);
          document.removeEventListener('keydown', enableAudioOnInteraction);
        } catch (error) {
          console.log("Play error:", error);
        }
      }
    };

    // Adicionar listeners para qualquer interação
    document.addEventListener('click', enableAudioOnInteraction);
    document.addEventListener('touchstart', enableAudioOnInteraction);
    document.addEventListener('keydown', enableAudioOnInteraction);

    return () => {
      document.removeEventListener('click', enableAudioOnInteraction);
      document.removeEventListener('touchstart', enableAudioOnInteraction);
      document.removeEventListener('keydown', enableAudioOnInteraction);
    };
  }, [isPlaying]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          console.log("Play error:", error);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <>
      {/* Audio Element - Música do site */}
      <audio
        ref={audioRef}
        autoPlay
        loop
        preload="auto"
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      >
        <source src="https://customer-assets.emergentagent.com/job_miguel-properties/artifacts/2yetlz3o_real-estate-construction-architecture-413278.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>

      {/* Controle Flutuante */}
      <div className="fixed bottom-8 right-8 z-40">
        <div className="flex flex-col items-center gap-2">
          {/* Label para deixar claro */}
          {isPlaying && (
            <div className="bg-black/90 text-gold px-3 py-1 rounded-full text-xs font-medium border border-gold/30 animate-pulse">
              🎵 Música tocando
            </div>
          )}
          
          <div className="flex gap-2">
            <Button
              onClick={togglePlay}
              size="lg"
              className={`shadow-2xl rounded-full w-16 h-16 p-0 transition-all duration-300 ${
                isPlaying 
                  ? 'bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-black' 
                  : 'bg-gray-800 hover:bg-gray-700 text-gold border-2 border-gold/30'
              }`}
              title={isPlaying ? "Pausar música" : "Tocar música"}
            >
              {isPlaying ? (
                <VolumeX className="h-6 w-6" />
              ) : (
                <Music className="h-6 w-6" />
              )}
            </Button>
            
            {isPlaying && (
              <Button
                onClick={toggleMute}
                size="lg"
                variant="outline"
                className="bg-black/80 border-gold/30 text-gold hover:bg-gold hover:text-black shadow-2xl rounded-full w-16 h-16 p-0"
                title={isMuted ? "Ativar som" : "Mutar"}
              >
                {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BackgroundMusic;
